import { useCallback } from 'react';
import {
  AiChatDto,
  AiChatMessage,
  AiTableArtifact,
  ChatShort,
} from '../../components/common/ai-chat/aiChatTypes';
import { useSweepApi } from '../sweep';
import { AgentContextComponent, AgentType, NewChatResponse } from '@server/ai';
import { AgentInputMode } from '../../components/aiAgentCenter/agent-input/agentInputModeTypes';
import {
  AgentFile,
  AiAgent,
  AiAgentWithFacts,
} from '../../components/aiAgentCenter/aiAgentsConsts';
import { PotentialIssue } from '@server/homepage';
import { Issue, MonitoringV2PotentialIssues } from '../../components/issues/issuesTypes';
import { IssueStatus } from '@server/issues';
import axios from 'axios';

export const useAiAgentApiFacade = () => {
  const sweepApi = useSweepApi();
  return {
    get_agents: useCallback(async () => {
      const { data } = await sweepApi.get<AiAgentWithFacts[]>(`/ai-agents`);
      return data;
    }, [sweepApi]),
    create_agent: useCallback(
      async (payload: {
        name: string;
        type: AgentType;
        contextComponents: AgentContextComponent;
        userInstructions: string | null;
        bestPracticeIds?: string[];
      }) => {
        const { data } = await sweepApi.post<AiAgent>(`/ai-agents`, payload);
        return data;
      },
      [sweepApi],
    ),
    rename_agent: useCallback(
      async (agentId: string, payload: { name: string }) =>
        sweepApi.patch<void>(`/ai-agents/${agentId}/name`, payload),
      [sweepApi],
    ),
    edit_agent_user_instructions: useCallback(
      async (
        agentId: string,
        payload: {
          userInstructions: string;
        },
      ) => sweepApi.patch<void>(`/ai-agents/${agentId}/user-instructions`, payload),
      [sweepApi],
    ),
    edit_system_instructions: useCallback(
      async (
        agentId: string,
        payload: {
          systemInstructions: string;
        },
      ) => sweepApi.patch<void>(`/ai-agents/${agentId}/system-instructions`, payload),
      [sweepApi],
    ),
    request_file_upload_url: useCallback(
      async (
        agentId: string,
        payload: {
          fileName: string;
          fileType: string;
          contentType: string;
        },
      ) => {
        const { data } = await sweepApi.post<{ uploadUrl: string; fileKey: string }>(
          `/ai-agents/${agentId}/files/upload-url`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    upload_file_to_url: useCallback(async (uploadUrl: string, file: File, contentType: string) => {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': contentType,
        },
      });
    }, []),
    confirm_file_upload: useCallback(
      async (
        agentId: string,
        payload: {
          fileKey: string;
          fileName: string;
          fileType: string;
        },
      ) => {
        const { data } = await sweepApi.post<AgentFile>(
          `/ai-agents/${agentId}/files/confirm-upload`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    delete_agent_file: useCallback(
      async (agentId: string, fileId: string) =>
        sweepApi.delete<void>(`/ai-agents/${agentId}/files/${fileId}`),
      [sweepApi],
    ),
    delete_agent: useCallback(
      async (agentId: string) => sweepApi.delete<void>(`/ai-agents/${agentId}`),
      [sweepApi],
    ),
    add_best_practices_to_agent: useCallback(
      async (agentId: string, bestPracticeIds: string[]) => {
        await sweepApi.post<void>(`/ai-agents/${agentId}/best-practices`, { bestPracticeIds });
      },
      [sweepApi],
    ),
    remove_best_practices_from_agent: useCallback(
      async (agentId: string, bestPracticeIds: string[]) => {
        await sweepApi.delete<void>(`/ai-agents/${agentId}/best-practices`, {
          data: { bestPracticeIds },
        });
      },
      [sweepApi],
    ),
    patch_agent_configurations: useCallback(
      async (
        agentId: string,
        payload: { includeConfigurations: { name: string; type: string }[] },
      ) => {
        const { data } = await sweepApi.patch<AiAgent>(
          `/ai-agents/${agentId}/configurations`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    get_chats: useCallback(
      async ({ agentId }: { agentId?: string }) => {
        const url = agentId ? `/ai-agents/${agentId}/chats` : `/ai-agents/chats`;
        const { data } = await sweepApi.get<ChatShort[]>(url);
        return data;
      },
      [sweepApi],
    ),
    get_chat: useCallback(
      async (chatId: string) => {
        const { data } = await sweepApi.get<AiChatDto>(`/ai-agents/chats/${chatId}`);
        return data;
      },
      [sweepApi],
    ),
    rename_chat: useCallback(
      async (chatId: string, payload: { name: string }) => {
        const { data } = await sweepApi.patch<boolean>(`/ai-agents/chats/${chatId}/name`, payload);
        return data;
      },
      [sweepApi],
    ),
    set_isFavorite_chat: useCallback(
      async (chatId: string, payload: { isFavorite: boolean }) => {
        const { data } = await sweepApi.patch<boolean>(
          `/ai-agents/chats/${chatId}/favorite`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    post_newChat: useCallback(
      async (
        agentId: string,
        payload: { message?: AiChatMessage; potentialIssues?: PotentialIssue[] },
        options?: { advancedMode?: AgentInputMode; additionalOrgIds?: string[] },
      ) => {
        const experimental = options?.advancedMode === AgentInputMode.ExperimentalMode;
        const research = options?.advancedMode === AgentInputMode.DeepDiveMode;
        const buildMode = options?.advancedMode === AgentInputMode.BuildMode;
        const additionalOrgIds = options?.additionalOrgIds;
        const body = {
          ...payload,
          ...(experimental && { experimental }),
          ...(research && { research }),
          ...(buildMode && { buildMode }),
          ...(additionalOrgIds?.length && { additionalOrgIds }),
        };
        const { data } = await sweepApi.post<NewChatResponse>(`/ai-agents/${agentId}/chats`, body);
        return data;
      },
      [sweepApi],
    ),
    get_issues: useCallback(
      async (agentId: string) => {
        const { data } = await sweepApi.get<MonitoringV2PotentialIssues>(
          `/ai-agents/monitoring/${agentId}/issues`,
        );
        return data;
      },
      [sweepApi],
    ),
    update_issueStatus: useCallback(
      async ({
        agentId,
        issueId,
        newStatus,
      }: {
        agentId: string;
        issueId: string;
        newStatus: IssueStatus;
      }) => {
        const payload = { status: newStatus };
        const { data } = await sweepApi.patch<Issue[]>(
          `/ai-agents/monitoring/${agentId}/issues/${issueId}`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    patch_sendMessage: useCallback(
      async (
        chatId: string,
        payload: { message: AiChatMessage },
        options?: { advancedMode?: AgentInputMode; additionalOrgIds?: string[] },
      ) => {
        const experimental = options?.advancedMode === AgentInputMode.ExperimentalMode;
        const research = options?.advancedMode === AgentInputMode.DeepDiveMode;
        const buildMode = options?.advancedMode === AgentInputMode.BuildMode;
        const additionalOrgIds = options?.additionalOrgIds;
        const body = {
          ...payload,
          ...(experimental && { experimental }),
          ...(research && { research }),
          ...(buildMode && { buildMode }),
          ...(additionalOrgIds?.length && { additionalOrgIds }),
        };
        return sweepApi.patch<void>(`/ai-agents/chats/${chatId}`, body);
      },
      [sweepApi],
    ),
    delete_chat: useCallback(
      async (chatId: string) => {
        const { data } = await sweepApi.delete<boolean>(`/ai-agents/chats/${chatId}`);
        return data;
      },
      [sweepApi],
    ),
    get_search: useCallback(
      async ({ searchText, agentId }: { searchText: string; agentId?: string }) => {
        const url = agentId
          ? `/ai-agents/${agentId}/chats/search?term=${encodeURIComponent(searchText)}`
          : `/ai-agents/chats/search?term=${encodeURIComponent(searchText)}`;
        const { data } = await sweepApi.get<AiChatDto[]>(url);
        return data;
      },
      [sweepApi],
    ),
    post_processMining: useCallback(
      async (funnelMapId: string, payload: { message: AiChatMessage }) => {
        const { data } = await sweepApi.post<{ message: string }>(
          `/ai-agents/process-mining/${funnelMapId}`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    delete_stream: useCallback(
      async (chatId: string) => {
        const { data } = await sweepApi.delete<boolean>(`/ai-agents/stream/${chatId}`);
        return data;
      },
      [sweepApi],
    ),
    get_artifact: useCallback(
      async (versionId: string) => {
        const { data } = await sweepApi.get<{
          id: string; // versionId
          code: string; // The artifact code
          createdAt: string;
          version: number;
        }>(`/ai-agents/artifacts/${versionId}`);
        return data;
      },
      [sweepApi],
    ),
    get_table_artifact: useCallback(
      async (tableId: string) => {
        const { data } = await sweepApi.get<AiTableArtifact>(
          `/ai-agents/table-artifacts/${tableId}`,
        );
        return data;
      },
      [sweepApi],
    ),
  };
};
